import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { getSettings, updateSettings } from '../../services/announcement.service';

const AnnouncementSettingsDialog = ({ open, onClose, intl, onSaved }) => {
  const [settings, setSettings] = useState({
    techniciancancreate: false,
    clientcanview: false,
    realtimenotificationsenabled: false,
    maxattachments: 5,
    maxattachmentsizemb: 10,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await getSettings();
      const data = r?.data?.data ?? r?.data ?? null;
      if (data) {
        setSettings({
          techniciancancreate: data.techniciancancreate || false,
          clientcanview: data.clientcanview || false,
          realtimenotificationsenabled: data.realtimenotificationsenabled === true,
          maxattachments: data.maxattachments || 5,
          maxattachmentsizemb: data.maxattachmentsizemb || 10,
        });
      }
      setLoading(false);
    } catch (e) {
      console.error('Error loading settings:', e);
      setError(intl.formatMessage({ id: 'process.error' }));
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        technicianCanCreate: settings.techniciancancreate,
        clientCanView: settings.clientcanview,
        realtimeNotificationsEnabled: settings.realtimenotificationsenabled,
        maxAttachments: Number(settings.maxattachments),
        maxAttachmentSizeMb: Math.min(10, Math.max(1, Number(settings.maxattachmentsizemb) || 1)),
      };

      const r = await updateSettings(payload);
      const savedSettings = r?.data?.data ?? r?.data ?? null;

      if (onSaved) {
        onSaved(savedSettings);
      }
      onClose();
      setSaving(false);
    } catch (e) {
      console.error('Error saving settings:', e);
      setError(intl.formatMessage({ id: 'process.error' }));
      setSaving(false);
    }
  };

  const switchRows = [
    {
      key: 'techniciancancreate',
      label: intl.formatMessage({ id: 'noticeboard.settings.technicianCanCreate' }),
    },
    {
      key: 'clientcanview',
      label: intl.formatMessage({ id: 'noticeboard.settings.clientCanView' }),
    },
    {
      key: 'realtimenotificationsenabled',
      label: intl.formatMessage({ id: 'noticeboard.settings.realtimeNotifications' }),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <SettingsIcon sx={{ color: 'primary.main' }} fontSize="small" />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
          {intl.formatMessage({ id: 'noticeboard.settings.title' })}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', overflow: 'hidden', bgcolor: 'background.paper' }}>
              {switchRows.map((row, idx) => (
                <FormControlLabel
                  key={row.key}
                  sx={{
                    m: 0,
                    width: '100%',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderTop: idx > 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={settings[row.key]}
                      onChange={(e) => setSettings({ ...settings, [row.key]: e.target.checked })}
                    />
                  }
                  label={<Typography sx={{ fontSize: 14 }}>{row.label}</Typography>}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                type="number"
                label={intl.formatMessage({ id: 'noticeboard.settings.maxAttachments' })}
                value={settings.maxattachments}
                onChange={(e) =>
                  setSettings({ ...settings, maxattachments: e.target.value })
                }
                inputProps={{ min: 1, max: 20 }}
                sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />

              <TextField
                type="number"
                label={intl.formatMessage({ id: 'noticeboard.settings.maxAttachmentSizeMb' })}
                value={settings.maxattachmentsizemb}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setSettings({ ...settings, maxattachmentsizemb: '' });
                    return;
                  }
                  let n = Number(raw);
                  if (Number.isNaN(n)) return;
                  if (n > 10) n = 10;
                  setSettings({ ...settings, maxattachmentsizemb: n });
                }}
                inputProps={{ min: 1, max: 10 }}
                helperText="Máximo de 10 MB"
                sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', borderRadius: '10px' }}>
          {intl.formatMessage({ id: 'noticeboard.action.cancel' })}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || saving}
          sx={{ textTransform: 'none', borderRadius: '10px' }}
        >
          {saving ? <CircularProgress size={24} /> : intl.formatMessage({ id: 'noticeboard.action.save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementSettingsDialog;
